/**
 * Packing Mode Guard - Global safety mechanism for CLIENT mode
 *
 * PURPOSE:
 * Ensures that NO scan-related API calls are made when in CLIENT packing mode.
 * This is a DEFENSIVE layer that catches any leaks from legacy code paths.
 *
 * USAGE:
 * 1. Call setPackingMode("CLIENT") when entering CLIENT mode
 * 2. Call setPackingMode("SERVER") when entering SERVER mode
 * 3. Call setPackingMode(null) when leaving the workspace
 * 4. Use assertNotClientMode() before any scan API call
 *
 * CRITICAL: This is a SAFETY NET, not the primary guard.
 * Primary guards should be in the component handlers.
 */

// Global packing mode state (singleton)
let currentPackingMode = null;
let packageId = null;

/**
 * Set the current packing mode
 * @param {"CLIENT" | "SERVER" | null} mode - The packing mode
 * @param {string} [pkgId] - Optional package ID for debugging
 */
export const setGlobalPackingMode = (mode, pkgId = null) => {
  const previousMode = currentPackingMode;
  currentPackingMode = mode;
  packageId = pkgId;

  if (process.env.NODE_ENV === "development") {
    console.log("[PACKING MODE GUARD] Mode changed", {
      from: previousMode,
      to: mode,
      packageId: pkgId,
    });
  }
};

/**
 * Get the current packing mode
 * @returns {"CLIENT" | "SERVER" | null}
 */
export const getGlobalPackingMode = () => currentPackingMode;

/**
 * Check if currently in CLIENT mode
 * @returns {boolean}
 */
export const isClientMode = () => currentPackingMode === "CLIENT";

/**
 * Check if currently in SERVER mode
 * @returns {boolean}
 */
export const isServerMode = () => currentPackingMode === "SERVER";

/**
 * Assert that we are NOT in CLIENT mode before making an API call
 * Throws an error if in CLIENT mode (should never happen if guards are working)
 *
 * @param {string} apiName - Name of the API being called (for error message)
 * @throws {Error} If in CLIENT mode
 */
export const assertNotClientMode = (apiName) => {
  if (currentPackingMode === "CLIENT") {
    const errorMsg = `[PACKING MODE GUARD] CRITICAL: ${apiName} called while in CLIENT mode! Package: ${packageId}`;
    console.error(errorMsg);
    console.trace("Stack trace for CLIENT mode API leak:");

    // In development, throw to make the error obvious
    if (process.env.NODE_ENV === "development") {
      throw new Error(errorMsg);
    }

    // In production, log but don't throw to avoid crashing
    // The API call will still fail on the server anyway
    return false;
  }
  return true;
};

/**
 * Clear the packing mode (call when leaving the workspace)
 */
export const clearGlobalPackingMode = () => {
  currentPackingMode = null;
  packageId = null;

  if (process.env.NODE_ENV === "development") {
    console.log("[PACKING MODE GUARD] Mode cleared");
  }
};

/**
 * List of scan-related API endpoints that should NEVER be called in CLIENT mode
 */
export const BLOCKED_ENDPOINTS_IN_CLIENT_MODE = [
  "BackOffice/package/scan/batch",
  "BackOffice/box/",
  "BackOffice/package/scan",
];

/**
 * Check if a URL is a scan-related endpoint
 * @param {string} url - The URL to check
 * @returns {boolean}
 */
export const isScanRelatedEndpoint = (url) => {
  if (!url) return false;
  return BLOCKED_ENDPOINTS_IN_CLIENT_MODE.some((endpoint) =>
    url.includes(endpoint)
  );
};

export default {
  setGlobalPackingMode,
  getGlobalPackingMode,
  isClientMode,
  isServerMode,
  assertNotClientMode,
  clearGlobalPackingMode,
  isScanRelatedEndpoint,
  BLOCKED_ENDPOINTS_IN_CLIENT_MODE,
};
